/**
 * Format a date string with timezone awareness, showing "Today" or "Tomorrow" when applicable
 * @param dateStr - ISO date string (typically in UTC from API)
 * @param timezone - IANA timezone name (e.g., "Europe/Zurich")
 * @param labels - Optional translated labels for "Today" and "Tomorrow"
 * @returns Formatted date string
 */
export const formatDate = (
  dateStr: string,
  timezone: string = "UTC",
  labels?: { today?: string; tomorrow?: string },
) => {
  const date = new Date(dateStr);

  // Get today and tomorrow in the restaurant's timezone
  const now = new Date();
  const todayInTz = new Date(
    now.toLocaleString("en-US", { timeZone: timezone }),
  );
  const tomorrowInTz = new Date(todayInTz);
  tomorrowInTz.setDate(tomorrowInTz.getDate() + 1);

  // Get the date in the restaurant's timezone
  const dateInTz = new Date(
    date.toLocaleString("en-US", { timeZone: timezone }),
  );

  const isToday = dateInTz.toDateString() === todayInTz.toDateString();
  const isTomorrow = dateInTz.toDateString() === tomorrowInTz.toDateString();

  if (isToday) return labels?.today ?? "Today";
  if (isTomorrow) return labels?.tomorrow ?? "Tomorrow";

  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: timezone,
  });
};

/**
 * Format a time string with timezone awareness
 * @param dateStr - ISO date string (typically in UTC from API)
 * @param timezone - IANA timezone name (e.g., "Europe/Zurich")
 * @returns Formatted time string
 */
export const formatTime = (dateStr: string, timezone: string = "UTC") =>
  new Date(dateStr).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: timezone,
  });

/**
 * Format relative time (e.g., "5m ago", "2h ago")
 * @param dateStr - ISO date string
 * @returns Relative time string
 */
export const formatRelativeTime = (dateStr: string) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  return `${diffDays} days ago`;
};

/**
 * Get today's date in a specific timezone
 * @param timezone - IANA timezone name (e.g., "Europe/Zurich")
 * @returns Date object representing "today" in the specified timezone
 */
export const getTodayInTimezone = (timezone: string = "UTC"): Date => {
  const now = new Date();
  const todayInTimezone = new Date(
    now.toLocaleString("en-US", { timeZone: timezone }),
  );
  return todayInTimezone;
};
